import axios from 'axios';

import {
  createClientOperationId,
  EventJobEventResponseDTO,
  EventJobStartResponseDTO,
  waitForEventJob,
} from '@/api/events/api';
import { createApiClient } from '@/api/http-client';
import { handleSessionExpired } from '@/api/session-expiry';
import { config } from '@/config';
import { createLogger } from '@/utils/logging';

const logger = createLogger('AssessmentAPI', 'api');

/**
 * Typed error thrown by every assessment API function; carries the HTTP status code when available.
 */
export class AssessmentApiError extends Error {
  status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AssessmentApiError';
    this.status = status;
  }
}

/** Request body for creating a new assessment under a project. */
export interface CreateAssessmentRequestDTO {
  id: string;
  title: string;
  engagement_context: string;
  stakeholder_list_markdown: string;
  supporting_doc_card_ids?: string[];
}

/** Partial update payload for an existing assessment; all fields are optional. */
export interface UpdateAssessmentRequestDTO {
  title?: string;
  engagement_context?: string;
  stakeholder_list_markdown?: string;
  interview_guide_markdown?: string;
  supporting_doc_card_ids?: string[];
  interview_transcript_card_ids?: string[];
  client_document_card_ids?: string[];
  analysis_options?: Record<string, unknown> | null;
}

/** LLM analysis configuration sent at assessment creation or update time. */
export interface AnalysisOptionsDTO {
  target_opportunity_signals?: string;
  maturity_framework?: string;
  additional_analysis_context?: string;
}

/** Full assessment record returned by the backend for create, get, and update calls. */
export interface AssessmentResponseDTO {
  assessment_id: string;
  client_id: string;
  project_id: string;
  title: string;
  engagement_context: string;
  current_step: number;
  status: string;
  stakeholder_list_card_id: string | null;
  stakeholder_list_markdown: string | null;
  supporting_doc_card_ids: string[];
  interview_guide_markdown: string | null;
  interview_guide_approved_at: string | null;
  interview_transcript_card_ids: string[];
  client_document_card_ids: string[];
  analysis_options: AnalysisOptionsDTO | null;
  final_analysis: string | null;
  last_modified_by: string | null;
  created_at: string;
  updated_at: string;
  step2_status?: 'intake' | 'generating' | 'failed' | 'review' | 'approved' | null;
  analysis_run_id?: string | null;
}

/** Paginated list of assessments returned by GET /clients/{c}/projects/{p}/assessments. */
export interface AssessmentListResponseDTO {
  items: AssessmentResponseDTO[];
  total: number;
}

/** Per-card result from the preflight validation endpoint. */
export interface CardPreflightResultDTO {
  card_id: string;
  valid: boolean;
  reason: string | null;
  recovery_action: string | null;
}

/** Response from the preflight validation endpoint. */
export interface AssessmentPreflightResponseDTO {
  step1_approved: boolean;
  transcript_gate_passed: boolean;
  transcript_results: CardPreflightResultDTO[];
  client_doc_results: CardPreflightResultDTO[];
}

/** Options for the interview guide generation request; all fields are optional overrides. */
export interface InterviewGuideGenerateRequestDTO {
  template_key?: string;
  provider_override?: string | null;
  agent_override?: string | null;
}

/** Response from the synchronous interview-guide generation endpoint; includes context-pack completeness. */
export interface InterviewGuideGenerateResponseDTO {
  assessment: AssessmentResponseDTO;
  context_pack_status: 'full' | 'partial';
  omitted_sections: string[];
}

const assessmentApiClient = createApiClient({
  baseURL: config.apiUrl,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Normalises any caught value into an AssessmentApiError, extracting the backend `detail` message
 * when available and delegating to session-expiry handling first.
 */
const parseAssessmentApiError = (error: unknown): AssessmentApiError => {
  handleSessionExpired(error);

  if (!axios.isAxiosError(error)) {
    const message = error instanceof Error ? error.message : 'Request failed';
    return new AssessmentApiError(message);
  }

  const status = error.response?.status;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return new AssessmentApiError(detail, status);
  }
  if (detail && typeof detail === 'object') {
    const nested = (detail as Record<string, unknown>).detail;
    if (typeof nested === 'string' && nested.trim().length > 0) {
      return new AssessmentApiError(nested, status);
    }
  }

  return new AssessmentApiError(error.message, status);
};

/** Returns the base URL segment for all assessment endpoints under a client/project. */
const assessmentBase = (clientId: string, projectId: string): string =>
  `/clients/${encodeURIComponent(clientId)}/projects/${encodeURIComponent(projectId)}/assessments`;

/**
 * Creates a new assessment under the given client/project.
 * POST /clients/{clientId}/projects/{projectId}/assessments
 *
 * @returns The newly created assessment record.
 */
export const createAssessment = async (
  payload: CreateAssessmentRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<AssessmentResponseDTO>(
      assessmentBase(clientId, projectId),
      payload,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to create assessment', parsed);
    throw parsed;
  }
};

/**
 * Applies a partial update to an existing assessment.
 * PATCH /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}
 *
 * @returns The updated assessment record.
 */
export const updateAssessment = async (
  assessmentId: string,
  payload: UpdateAssessmentRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentResponseDTO> => {
  try {
    const response = await assessmentApiClient.patch<AssessmentResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${encodeURIComponent(assessmentId)}`,
      payload,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to update assessment', parsed);
    throw parsed;
  }
};

/**
 * Fetches a single assessment by ID.
 * GET /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}
 *
 * @returns The full assessment record.
 */
export const getAssessment = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentResponseDTO> => {
  try {
    const response = await assessmentApiClient.get<AssessmentResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${encodeURIComponent(assessmentId)}`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to get assessment', parsed);
    throw parsed;
  }
};

/**
 * Lists all assessments for the given client/project.
 * GET /clients/{clientId}/projects/{projectId}/assessments
 *
 * @returns Paginated list of assessment records.
 */
export const listAssessments = async (
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentListResponseDTO> => {
  try {
    const response = await assessmentApiClient.get<AssessmentListResponseDTO>(
      assessmentBase(clientId, projectId),
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to list assessments', parsed);
    throw parsed;
  }
};

/**
 * Generates an interview guide synchronously (long-poll, 5 min timeout).
 * POST /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}/interview-guide/generate
 *
 * @returns The updated assessment plus context-pack completeness metadata.
 */
export const generateInterviewGuide = async (
  assessmentId: string,
  payload: InterviewGuideGenerateRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<InterviewGuideGenerateResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<InterviewGuideGenerateResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${encodeURIComponent(assessmentId)}/interview-guide/generate`,
      payload,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 300000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to generate interview guide', parsed);
    throw parsed;
  }
};

/**
 * Enqueues an async interview-guide generation job and returns the job handle for SSE streaming.
 * POST /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}/interview-guide/jobs
 *
 * @param clientOperationId - Idempotency key; auto-generated when omitted.
 * @returns Job start metadata including the stream URL.
 */
export const startInterviewGuideJob = async (
  assessmentId: string,
  payload: InterviewGuideGenerateRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
  clientOperationId = createClientOperationId('interview-guide'),
): Promise<EventJobStartResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<EventJobStartResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${encodeURIComponent(assessmentId)}/interview-guide/jobs`,
      { client_operation_id: clientOperationId, payload },
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        timeout: 15000,
      },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to start interview guide job', parsed);
    throw parsed;
  }
};

/**
 * End-to-end helper: starts an interview-guide job, then streams SSE events until the job
 * reaches a terminal state, forwarding each event to the optional `onEvent` callback.
 *
 * @param onEvent - Optional callback invoked for each SSE event during streaming.
 * @returns Context-pack status and any omitted sections from the final job snapshot.
 */
export const generateInterviewGuideViaEvents = async (
  assessmentId: string,
  payload: InterviewGuideGenerateRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
  onEvent?: (event: EventJobEventResponseDTO) => void,
): Promise<{ context_pack_status: 'full' | 'partial'; omitted_sections: string[] }> => {
  const started = await startInterviewGuideJob(
    assessmentId,
    payload,
    accessToken,
    clientId,
    projectId,
  );
  const snapshot = await waitForEventJob<{
    context_pack_status: 'full' | 'partial';
    omitted_sections: string[];
  }>(accessToken, started, onEvent);
  const result = snapshot.result;
  if (!result?.context_pack_status) {
    throw new AssessmentApiError('Unexpected job result: missing context_pack_status');
  }
  return result;
};

/**
 * Validates each linked card for the given assessment and returns per-card processability results.
 * POST /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}/preflight
 */
export const runAssessmentPreflight = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentPreflightResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<AssessmentPreflightResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/preflight`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to run assessment preflight', parsed);
    throw parsed;
  }
};

// ── Spec 0094 — Transcript Analysis Step 2 ───────────────────────────────────

export interface TranscriptAnalysisStartRequestDTO {
  transcript_card_ids: string[];
  client_doc_card_ids: string[];
  analysis_options: Record<string, unknown>;
  skill_key: string;
}

export interface TranscriptAnalysisStartResponseDTO {
  job_id: string;
  status: string;
  stream_url: string;
  job_stream_url: string;
  status_url: string;
  cancel_url: string;
}

/**
 * Start or resume a durable transcript analysis run.
 * POST /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}/step-2/start
 *
 * Idempotent: repeating with the same transcripts and skill_key returns the same job.
 */
export const startTranscriptAnalysis = async (
  assessmentId: string,
  payload: TranscriptAnalysisStartRequestDTO,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<TranscriptAnalysisStartResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<TranscriptAnalysisStartResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/step-2/start`,
      payload,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to start transcript analysis', parsed);
    throw parsed;
  }
};

// ── Step 2 artifact DTOs ─────────────────────────────────────────────────────

export interface ValidationSummaryDTO {
  passed: boolean;
  failed_checks: string[];
  hints: string[];
}

export interface TranscriptAnalysisArtifactResponseDTO {
  markdown: string;
  revision: number;
  validation: ValidationSummaryDTO;
  approval_state: 'pending' | 'approved';
  approved_revision: number | null;
}

export interface UpdateArtifactResponseDTO {
  revision: number;
  markdown: string;
  validation: ValidationSummaryDTO;
}

export interface ApproveArtifactResponseDTO {
  step2_status: 'approved';
  approved_revision: number;
}

export interface Step2StatusResponseDTO {
  step2_status: 'intake' | 'generating' | 'failed' | 'review' | 'approved';
  analysis_run_id: string | null;
}

/** GET /step-2/artifact — fetch the current transcript analysis artifact. */
export const getArtifact = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<TranscriptAnalysisArtifactResponseDTO> => {
  try {
    const response = await assessmentApiClient.get<TranscriptAnalysisArtifactResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/step-2/artifact`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to get step-2 artifact', parsed);
    throw parsed;
  }
};

/** PUT /step-2/artifact — update the artifact Markdown and get fresh validation. */
export const updateArtifact = async (
  assessmentId: string,
  markdown: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<UpdateArtifactResponseDTO> => {
  try {
    const response = await assessmentApiClient.put<UpdateArtifactResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/step-2/artifact`,
      { markdown },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to update step-2 artifact', parsed);
    throw parsed;
  }
};

/** POST /step-2/approve — approve the artifact when validation passes. */
export const approveArtifact = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<ApproveArtifactResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<ApproveArtifactResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/step-2/approve`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to approve step-2 artifact', parsed);
    throw parsed;
  }
};

/** GET /step-2/status — current step2_status and active run identity. */
export const getStep2Status = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<Step2StatusResponseDTO> => {
  try {
    const response = await assessmentApiClient.get<Step2StatusResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/step-2/status`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to get step-2 status', parsed);
    throw parsed;
  }
};

/**
 * Stamps the interview guide as approved, locking it for transcript upload.
 * POST /clients/{clientId}/projects/{projectId}/assessments/{assessmentId}/interview-guide/approve
 *
 * @returns The updated assessment record with `interview_guide_approved_at` set.
 */
export const approveInterviewGuide = async (
  assessmentId: string,
  accessToken: string,
  clientId: string,
  projectId: string,
): Promise<AssessmentResponseDTO> => {
  try {
    const response = await assessmentApiClient.post<AssessmentResponseDTO>(
      `${assessmentBase(clientId, projectId)}/${assessmentId}/interview-guide/approve`,
      {},
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    return response.data;
  } catch (error) {
    const parsed = parseAssessmentApiError(error);
    logger.error('Failed to approve interview guide', parsed);
    throw parsed;
  }
};
